import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Plus, FolderOpen, Notebook, Filter, Loader2,
} from 'lucide-react';
import AppShell from '@/components/AppShell';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import FolderRail from '@/components/library/FolderRail';
import LessonGrid from '@/components/library/LessonGrid';
import FolderDialog from '@/components/library/FolderDialog';
import LessonViewerModal from '@/components/library/LessonViewerModal';
import MoveLessonDialog from '@/components/library/MoveLessonDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ARTIFACT_FILTERS = [
  { value: 'all', label: 'All types' },
  { value: 'lesson_plan', label: 'Lesson plans' },
  { value: 'skeleton_notes', label: 'Skeleton notes' },
  { value: 'rubric', label: 'Rubrics' },
];

export default function LibraryPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [folders, setFolders] = useState([]);
  const [uncategorizedCount, setUncategorizedCount] = useState(0);
  const [lessons, setLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lessonsLoading, setLessonsLoading] = useState(false);

  // Selection state — 'all' | 'root' | folder_id
  const [activeFolder, setActiveFolder] = useState(params.get('folder') || 'all');
  const [artifactType, setArtifactType] = useState(params.get('type') || 'all');
  const [search, setSearch] = useState('');

  // Dialog state
  const [folderDialogOpen, setFolderDialogOpen] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [viewingLesson, setViewingLesson] = useState(null);
  const [movingLesson, setMovingLesson] = useState(null);

  const fetchFolders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/library/folders`);
      setFolders(data.folders || []);
      setUncategorizedCount(data.uncategorized_count || 0);
    } catch (err) {
      toast.error('Failed to load folders');
    }
  }, []);

  const fetchLessons = useCallback(async () => {
    setLessonsLoading(true);
    try {
      const query = new URLSearchParams();
      if (activeFolder === 'root') query.set('folder_id', 'root');
      else if (activeFolder !== 'all') query.set('folder_id', activeFolder);
      if (artifactType !== 'all') query.set('artifact_type', artifactType);
      if (search.trim()) query.set('q', search.trim());
      const { data } = await axios.get(`${API}/library/lessons?${query.toString()}`);
      setLessons(data);
    } catch (err) {
      toast.error('Failed to load lessons');
    } finally {
      setLessonsLoading(false);
    }
  }, [activeFolder, artifactType, search]);

  // Initial load
  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchFolders(), fetchLessons()]);
      setLoading(false);
    })();
  }, [fetchFolders, fetchLessons]);

  // Filter changes
  useEffect(() => { fetchLessons(); }, [fetchLessons]);

  // Keep URL query params in sync so the view survives refresh
  useEffect(() => {
    const next = new URLSearchParams();
    if (activeFolder !== 'all') next.set('folder', activeFolder);
    if (artifactType !== 'all') next.set('type', artifactType);
    setParams(next, { replace: true });
  }, [activeFolder, artifactType, setParams]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => fetchLessons(), 250);
    return () => clearTimeout(t);
  }, [search, fetchLessons]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleCreateFolder = async ({ name, color }) => {
    try {
      await axios.post(`${API}/library/folders`, { name, color });
      toast.success(`Folder "${name}" created`);
      await fetchFolders();
      setFolderDialogOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create folder');
    }
  };

  const handleUpdateFolder = async (folderId, patch) => {
    try {
      await axios.patch(`${API}/library/folders/${folderId}`, patch);
      toast.success('Folder updated');
      await fetchFolders();
      setEditingFolder(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update folder');
    }
  };

  const handleDeleteFolder = async (folder) => {
    if (!window.confirm(`Delete folder "${folder.name}"? Its lessons will move to Uncategorized.`)) return;
    try {
      await axios.delete(`${API}/library/folders/${folder.id}`);
      toast.success(`Deleted "${folder.name}"`);
      if (activeFolder === folder.id) setActiveFolder('all');
      await Promise.all([fetchFolders(), fetchLessons()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete folder');
    }
  };

  const handleOpenLesson = async (lesson) => {
    try {
      const { data } = await axios.get(`${API}/library/lessons/${lesson.id}`);
      setViewingLesson(data);
    } catch {
      toast.error('Failed to load lesson');
    }
  };

  const handleUpdateLesson = async (lessonId, patch) => {
    try {
      const { data } = await axios.patch(`${API}/library/lessons/${lessonId}`, patch);
      await Promise.all([fetchFolders(), fetchLessons()]);
      if (viewingLesson?.id === lessonId) setViewingLesson(data);
      return data;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save changes');
      throw err;
    }
  };

  const handleDeleteLesson = async (lesson) => {
    if (!window.confirm(`Delete "${lesson.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/library/lessons/${lesson.id}`);
      toast.success('Deleted from library');
      if (viewingLesson?.id === lesson.id) setViewingLesson(null);
      await Promise.all([fetchFolders(), fetchLessons()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const totalLessons = folders.reduce((n, f) => n + (f.lesson_count || 0), 0) + uncategorizedCount;

  if (loading) {
    return (
      <AppShell>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#14213D]" />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-8" data-testid="library-page">
        {/* Header */}
        <div className="flex items-start sm:items-center justify-between gap-4 mb-6 sm:mb-8 flex-col sm:flex-row">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md bg-[#14213D] flex items-center justify-center flex-shrink-0">
              <Notebook className="h-5 w-5 text-[#D4AF37]" strokeWidth={1.75} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold">Workspace</p>
              <h1 className="font-serif-display text-2xl sm:text-3xl text-[#14213D] tracking-tight">My Library</h1>
              <p className="text-xs sm:text-sm text-[#5A6577] mt-0.5">
                {totalLessons} saved {totalLessons === 1 ? 'item' : 'items'} across {folders.length} {folders.length === 1 ? 'folder' : 'folders'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <Button
              onClick={() => { setEditingFolder(null); setFolderDialogOpen(true); }}
              variant="outline"
              size="sm"
              className="border-[#D4AF37]/40 text-[#14213D] hover:bg-[#FBF6E8] flex-shrink-0"
              data-testid="new-folder-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> New folder
            </Button>
            <Button
              onClick={() => navigate('/ai-generate')}
              size="sm"
              className="bg-[#14213D] hover:bg-[#1D2D50] text-[#FDFBF7] flex-shrink-0"
              data-testid="generate-new-btn"
            >
              <Plus className="h-4 w-4 mr-1.5" /> Generate new
            </Button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="bg-white border border-[#EBE7E0] rounded-lg p-3 sm:p-4 mb-6 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#5A6577]" />
            <Input
              placeholder="Search saved lessons, notes, rubrics…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 border-[#EBE7E0] bg-[#FDFBF7]"
              data-testid="library-search-input"
            />
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Filter className="h-4 w-4 text-[#5A6577]" />
            <Select value={artifactType} onValueChange={setArtifactType}>
              <SelectTrigger className="w-[160px] h-9 border-[#EBE7E0] bg-[#FDFBF7]" data-testid="library-type-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ARTIFACT_FILTERS.map((f) => (
                  <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Main layout */}
        <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6">
          <FolderRail
            folders={folders}
            uncategorizedCount={uncategorizedCount}
            active={activeFolder}
            onSelect={setActiveFolder}
            onEdit={(f) => { setEditingFolder(f); setFolderDialogOpen(true); }}
            onDelete={handleDeleteFolder}
          />

          <div className="min-w-0">
            {lessonsLoading ? (
              <div className="min-h-[300px] flex items-center justify-center bg-white border border-[#EBE7E0] rounded-lg">
                <Loader2 className="h-6 w-6 animate-spin text-[#14213D]" />
              </div>
            ) : (
              <LessonGrid
                lessons={lessons}
                folders={folders}
                onOpen={handleOpenLesson}
                onMove={(l) => setMovingLesson(l)}
                onDelete={handleDeleteLesson}
                onRename={async (l, newTitle) => {
                  if (!newTitle || newTitle === l.title) return;
                  await handleUpdateLesson(l.id, { title: newTitle });
                  toast.success('Renamed');
                }}
                onGenerateNew={() => navigate('/ai-generate')}
                emptyContext={{
                  hasFolder: activeFolder !== 'all',
                  filterApplied: artifactType !== 'all' || !!search.trim(),
                }}
              />
            )}
          </div>
        </div>

        {/* Dialogs */}
        <FolderDialog
          open={folderDialogOpen}
          folder={editingFolder}
          onClose={() => { setFolderDialogOpen(false); setEditingFolder(null); }}
          onCreate={handleCreateFolder}
          onUpdate={handleUpdateFolder}
        />
        <LessonViewerModal
          lesson={viewingLesson}
          folders={folders}
          onClose={() => setViewingLesson(null)}
          onUpdate={handleUpdateLesson}
          onDelete={handleDeleteLesson}
        />
        <MoveLessonDialog
          lesson={movingLesson}
          folders={folders}
          onClose={() => setMovingLesson(null)}
          onMove={async (folderId) => {
            await handleUpdateLesson(movingLesson.id, { folder_id: folderId });
            toast.success('Moved');
            setMovingLesson(null);
          }}
        />
      </div>
    </AppShell>
  );
}
